import React from 'react';
import './analyticsAsk.css';

// Human-test feedback (session #113): 'Directional' read as jargon — badge copy stays in
// data-amount language. The contract tier name is still 'directional' server-side.
const BADGE_LABEL = { green: 'Solid', amber: 'Limited data', red: 'Not enough data yet' };

/** Pure render of an `answer` presentation. Renders server-formatted fields verbatim — never
 *  recomputes numbers.
 *
 *  `p.plain_english` (#1760) is a jargon-free, one-sentence summary — server-templated, no
 *  LLM — rendered as its OWN line ABOVE the technical card (never instead of it): a
 *  non-technical facilitator reads the finding first, then the authoritative badge/figure/
 *  disclosure block underneath is unchanged. This also generalises the older WARN-DON'T-BLOCK
 *  (#1657) placement rule — the number was already never allowed to be the first thing read;
 *  putting the summary above the whole card (not just above the figure) satisfies that even
 *  more directly.
 *
 *  `p.empty` (genuine no-data/no-delta, answer.value === null) — the figure area renders a
 *  neutral placeholder; no warning styling, there's simply nothing to disclose yet.
 *
 *  `p.suppressed && !p.empty` (warn-don't-block, #1657) — the point estimate IS disclosed, but
 *  styled red/warn (figure colour + a warn-styled summary line); the CI renders below the
 *  figure too. */
export default function AnswerCard({ presentation: p }) {
  if (!p) return null;
  const d = p.disclosure || {};
  const warnedRed = p.suppressed && !p.empty;
  return (
    <>
      <p className={`aa-answer__summary${warnedRed ? ' aa-answer__summary--warn' : ''}`}>{p.plain_english}</p>
      <div className={`aa-answer aa-answer--${p.badge}`}>
        <div className="aa-answer__top">
          <span className={`aa-badge aa-badge--${p.badge}`}><span className="aa-badge__dot" />{BADGE_LABEL[p.badge] || p.badge}</span>
          <div className="aa-answer__headline">{p.headline}</div>
          <div className={`aa-answer__figure${p.empty ? ' aa-answer__figure--empty' : ''}${warnedRed ? ' aa-answer__figure--warn' : ''}`}>
            {p.empty ? '— nothing to show yet —' : p.value_label}
          </div>
          {warnedRed && d.confidence_interval_label && <div className="aa-answer__ci">{d.confidence_interval_label}</div>}
        </div>
        <div className="aa-why">
          <div className="aa-why__label">Why this answer</div>
          <dl className="aa-why__grid">
            <div><dt>Scope</dt><dd>{d.scope}</dd></div>
            <div><dt>Window</dt><dd>{d.window}</dd></div>
            <div><dt>Sample size</dt><dd>{d.sample_size}</dd></div>
            {d.confidence_interval_label && <div><dt>Range</dt><dd>{d.confidence_interval_label}</dd></div>}
            <div className="aa-why__full"><dt>Definition</dt><dd>{d.metric_definition}</dd></div>
          </dl>
        </div>
      </div>
    </>
  );
}
